import React, { useState, useEffect, useRef } from 'react';
import { S } from '../styles';
import { getTheme } from '../theme';
import { API, APP, buildEmailAction, DEFAULT_SMS_TEMPLATES, authHeaders, startLightspeedConnect, startStripeConnect } from '../utils/helpers';
import { SettingsContext } from './settings/context';
import SettingsShop from './settings/SettingsShop';
import SettingsConnections from './settings/SettingsConnections';
import SettingsPayments from './settings/SettingsPayments';
import SettingsBooking from './settings/SettingsBooking';
import SettingsMessages from './settings/SettingsMessages';
import SettingsWorkflow from './settings/SettingsWorkflow';
import SettingsUsers from './settings/SettingsUsers';
import SettingsWebsite from './settings/SettingsWebsite';
import { SETTINGS_GROUPS, QUEUE_KEYS, groupById, groupIsDirty } from './settings/groups';

const GROUP_COMPONENTS = {
  shop: SettingsShop, connections: SettingsConnections, payments: SettingsPayments,
  booking: SettingsBooking, messages: SettingsMessages, workflow: SettingsWorkflow,
  users: SettingsUsers, website: SettingsWebsite,
};

// `group` and the two callbacks come from App.js, which owns the sidebar: the
// open group is a navigation position, and the unsaved-changes dialog has to be
// able to stop a click on Service Queue as readily as one on another group.
function SettingsPage({ theme = 'dark', authStatus = null, currentUser = null,
                        group = 'shop', onDirtyChange = null, saveSignal = 0 }) {
  const [showConnAdvanced, setShowConnAdvanced] = useState(false);
  const T = getTheme(theme);
  const isDark = theme === 'dark';
  const [allStatuses, setAllStatuses] = useState([]);
  const [paymentTypes, setPaymentTypes] = useState([]);
  const [taxClasses, setTaxClasses] = useState([]);
  const [lsDefaults, setLsDefaults] = useState({ employees: [], registers: [], shops: [] });
  const [settings, setSettings] = useState(null);

  // Every booking link this page hands the shop NAMES the shop: /booking/<slug>.
  // A bare /booking only resolves while exactly one shop exists, so a link
  // copied today would quietly stop working the day another shop is created.
  // `shopSlug` comes from GET /api/settings; the bare form is the fallback so a
  // missing slug never produces a link with "undefined" in it.
  const bookingBase = `${APP}/booking${settings?.shopSlug ? `/${settings.shopSlug}` : ''}`;
  const [saved, setSaved] = useState(false);
  // What went wrong, when the save did not happen. Held rather than flashed:
  // the settings are still on screen unsaved, and the person needs to know
  // that before they navigate away believing the work is stored.
  const [saveError, setSaveError] = useState(null);
  // "refresh the queue to apply" used to print after EVERY save, including a
  // change to the shop phone number or a register key, neither of which the
  // queue has ever read. It is true only for the settings below: the Service
  // Queue loads its tickets when opened and does not re-check, so a queue left
  // open keeps the old arrangement, and the server holds ticket data for 30
  // seconds besides. Everything else needs no refresh, and saying otherwise
  // trains people to ignore the line.
  const [queueAffected, setQueueAffected] = useState(false);
  const [loading, setLoading] = useState(true);
  // The settings as last stored on the server: what GET returned, then each
  // successful POST. Used only to work out what a save actually changed.
  const savedSnapshotRef = useRef(null);
  // The same value as state, because the save bar and the sidebar's dirty dot
  // both have to re-render when it moves.
  const [savedSettings, setSavedSettings] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [laborItems, setLaborItems] = useState([]);
  const [loadingItems, setLoadingItems] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null); // template being edited
  const [showNewTemplate, setShowNewTemplate] = useState(false);
  const [newTemplateName, setNewTemplateName] = useState('');
  const [newItemLabel, setNewItemLabel] = useState('');
  const [showPaste, setShowPaste] = useState(false);
  const [pasteText, setPasteText] = useState('');
  const [newBlackoutDate, setNewBlackoutDate] = useState('');
  const [bookingItems, setBookingItems] = useState([]);
  const [loadingBookingItems, setLoadingBookingItems] = useState(false);
  const [bookingItemFilter, setBookingItemFilter] = useState('');
  const [copiedLinkId, setCopiedLinkId] = useState(null);
  const [stripeStatus, setStripeStatus] = useState(null);
  const [stripeBanner, setStripeBanner] = useState(null); // { type: 'success'|'error', text }
  const [terminals, setTerminals] = useState([]);
  const [terminalsLoading, setTerminalsLoading] = useState(true);
  const [newTerminalLabel, setNewTerminalLabel] = useState('');
  const [terminalBusy, setTerminalBusy] = useState(false);
  const [terminalError, setTerminalError] = useState(null);
  // Revoked keys stay in the database on purpose: each texted receipt records
  // which register sent it, and a machine still holding an old key is told it
  // was revoked rather than that it is unknown. So they are tucked away here,
  // not deleted. Collapsed by default; the list is about the registers in use.
  const [showRevokedTerminals, setShowRevokedTerminals] = useState(false);
  // The whole key, held in memory only, from the moment it is generated until
  // the person dismisses it. It is never fetched again because no route can
  // return it again.
  const [freshKey, setFreshKey] = useState(null);
  const [keyCopied, setKeyCopied] = useState(false);

  // Shorthand for updating nested booking settings
  const setBooking = (patch) => {
    setSettings(prev => ({ ...prev, booking: { ...(prev.booking || {}), ...patch } }));
    setSaved(false);
  };

  // Same pattern for payment settings — kept separate from booking since
  // payment links work whether or not online booking is enabled.
  const setPayments = (patch) => {
    setSettings(prev => ({ ...prev, payments: { ...(prev.payments || {}), ...patch } }));
    setSaved(false);
  };

  // Lightspeed account defaults — employee/register/shop IDs written onto every
  // Sale Spinboard creates. Separate from payments: these identify WHO and WHERE
  // the sale is recorded, not how the money is classified.
  const setLightspeed = (patch) => {
    setSettings(prev => ({ ...prev, lightspeed: { ...(prev.lightspeed || {}), ...patch } }));
    setSaved(false);
  };

  const AR_DEFAULT_MSG = "Thanks for reaching out! We're closed right now. Our hours are {shopHours}. We'll get back to you when we're open.";

  const setAutoReply = (patch) => {
    setSettings(prev => ({ ...prev, autoReply: { ...(prev.autoReply || {}), ...patch } }));
    setSaved(false);
  };

  // Structured hours live on the shop profile — they're the shop's hours, not
  // the auto-responder's, and anything else that needs to know whether the
  // shop is open will want them there too.
  const setShopHoursDay = (index, patch) => {
    setSettings(prev => {
      const sp = prev.shopProfile || {};
      const days = [...(sp.hoursStructured || [])];
      while (days.length < 7) days.push({ closed: true, open: '10:00', close: '18:00' });
      days[index] = { ...days[index], ...patch };
      return { ...prev, shopProfile: { ...sp, hoursStructured: days } };
    });
    setSaved(false);
  };

  const loadBookingItems = async () => {
    setLoadingBookingItems(true);
    try {
      // Always fresh: this list is opened to add an item just made in
      // Lightspeed, which the 15-minute labor items cache would hide.
      const res = await fetch(`${API}/api/labor-items?refresh=true`, { credentials:'include', headers: authHeaders() });
      const data = await res.json();
      setBookingItems(Array.isArray(data) ? data : []);
    } catch (_) {}
    setLoadingBookingItems(false);
  };

  const parsePasteText = () => {
    if (!pasteText.trim() || !editingTemplate) return;
    const lines = pasteText
      .split('\n')
      .map(l => l.trim())
      // Remove empty lines, lines that are just punctuation, prices, asterisk notes
      .filter(l => l.length > 2 && !l.startsWith('*') && !l.startsWith('$') && !/^\d+\.?\d*$/.test(l));
    const newItems = lines.map((label, i) => ({
      id: `item_${Date.now()}_${i}`,
      label,
      custom: false,
    }));
    const updated = { ...editingTemplate, items: [...editingTemplate.items, ...newItems] };
    setEditingTemplate(updated);
    setPasteText('');
    setShowPaste(false);
  };

  useEffect(() => {
    Promise.all([
      fetch(`${API}/api/workorder-statuses`, { credentials:'include', headers: authHeaders() }).then(r => r.json()),
      fetch(`${API}/api/settings`, { credentials:'include', headers: authHeaders() }).then(r => r.json()),
      fetch(`${API}/api/templates`, { credentials:'include', headers: authHeaders() }).then(r => r.json()),
      // Non-fatal: a shop with no Lightspeed connection still gets Settings,
      // just with an empty dropdown and its explanatory warning.
      fetch(`${API}/api/payment-types`, { credentials:'include', headers: authHeaders() })
        .then(r => r.ok ? r.json() : { paymentTypes: [] })
        .catch(() => ({ paymentTypes: [] })),
      fetch(`${API}/api/tickets/tax-classes/list`, { credentials:'include', headers: authHeaders() })
        .then(r => r.ok ? r.json() : [])
        .catch(() => []),
      fetch(`${API}/api/lightspeed-defaults`, { credentials:'include', headers: authHeaders() })
        .then(r => r.ok ? r.json() : { employees: [], registers: [], shops: [] })
        .catch(() => ({ employees: [], registers: [], shops: [] })),
    ]).then(([statuses, s, tpls, pt, tc, lsd]) => {
      setAllStatuses(Array.isArray(statuses) ? statuses : []);
      setSettings(s);
      // The baseline for "did this save touch the queue" -- what the server
      // has, before anyone edits anything.
      savedSnapshotRef.current = s;
      setSavedSettings(s);
      setTemplates(Array.isArray(tpls) ? tpls : []);
      setPaymentTypes(pt?.paymentTypes || []);
      setTaxClasses(Array.isArray(tc) ? tc : []);
      setLsDefaults(lsd || { employees: [], registers: [], shops: [] });
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  useEffect(() => {
    fetch(`${API}/api/stripe-connect/status`, { credentials:'include', headers: authHeaders() })
      .then(r => r.json())
      .then(setStripeStatus)
      .catch(() => setStripeStatus({ connected:false }));

    // Land here after the OAuth redirect — show a banner once, then clean the URL
    const params = new URLSearchParams(window.location.search);
    if (params.get('stripe') === 'connected') {
      setStripeBanner({ type:'success', text:'Stripe account connected.' });
      window.history.replaceState({}, '', window.location.pathname);
    } else if (params.get('stripe') === 'error') {
      setStripeBanner({ type:'error', text:`Stripe connection failed: ${params.get('reason') || 'unknown error'}` });
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const loadTerminals = async () => {
    try {
      const res = await fetch(`${API}/api/terminals`, { credentials:'include', headers: authHeaders() });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setTerminals(await res.json());
    } catch {
      setTerminals([]);
    } finally {
      setTerminalsLoading(false);
    }
  };

  useEffect(() => { loadTerminals(); }, []);

  const createTerminal = async () => {
    const label = newTerminalLabel.trim();
    if (!label) { setTerminalError('Give the terminal a name first.'); return; }
    setTerminalBusy(true);
    setTerminalError(null);
    try {
      const res = await fetch(`${API}/api/terminals`, {
        method:'POST', credentials:'include', headers: authHeaders(true),
        body: JSON.stringify({ label }),
      });
      const body = await res.json();
      if (!res.ok) throw new Error(body.error || `HTTP ${res.status}`);
      setFreshKey({ key: body.key, label: body.label });
      setKeyCopied(false);
      setNewTerminalLabel('');
      await loadTerminals();
    } catch (err) {
      setTerminalError(err.message);
    } finally {
      setTerminalBusy(false);
    }
  };

  const revokeTerminal = async (t) => {
    if (!window.confirm(`Revoke "${t.label}"? The register using this key stops being able to text receipts immediately, and the key cannot be un-revoked — that terminal will need a new one.`)) return;
    setTerminalError(null);
    try {
      const res = await fetch(`${API}/api/terminals/${t.id}/revoke`, {
        method:'POST', credentials:'include', headers: authHeaders(),
      });
      const body = await res.json();
      if (!res.ok) throw new Error(body.error || `HTTP ${res.status}`);
      await loadTerminals();
    } catch (err) {
      setTerminalError(err.message);
    }
  };

  const disconnectStripe = async () => {
    if (!window.confirm("Disconnect this Stripe account? This only clears Spinboard's record so you can reconnect — it doesn't affect deposits already collected.")) return;
    await fetch(`${API}/api/stripe-connect/disconnect`, { method:'POST', credentials:'include', headers: authHeaders() });
    setStripeStatus({ connected:false });
  };

  const loadLaborItems = async () => {
    setLoadingItems(true);
    try {
      const res = await fetch(`${API}/api/labor-items`, { credentials:'include', headers: authHeaders() });
      const data = await res.json();
      setLaborItems(Array.isArray(data) ? data : []);
    } catch (_) {}
    setLoadingItems(false);
  };

  const importFromItem = async (item) => {
    // Parse longDescription line by line into checklist items
    const raw = item.longDescription || item.name || '';
    const lines = raw.split('\n').map(l => l.trim()).filter(l => l.length > 0 && !l.startsWith('*'));
    const tplItems = lines.map((label, i) => ({ id: `item_${Date.now()}_${i}`, label, custom: false }));
    const res = await fetch(`${API}/api/templates`, {
      method:'POST', credentials:'include',
      headers: authHeaders(true),
      body: JSON.stringify({ name: item.name, items: tplItems, sourceItemId: item.id }),
    });
    const newTpl = await res.json();
    setTemplates(prev => [...prev, newTpl]);
    setEditingTemplate(newTpl);
    setLaborItems([]);
  };

  const saveTemplate = async (tpl) => {
    await fetch(`${API}/api/templates/${tpl.id}`, {
      method:'PUT', credentials:'include',
      headers: authHeaders(true),
      body: JSON.stringify(tpl),
    });
    setTemplates(prev => prev.map(t => t.id === tpl.id ? tpl : t));
    setEditingTemplate(null);
  };

  const deleteTemplate = async (id) => {
    if (!window.confirm('Delete this template?')) return;
    await fetch(`${API}/api/templates/${id}`, { method:'DELETE', credentials:'include', headers: authHeaders() });
    setTemplates(prev => prev.filter(t => t.id !== id));
    if (editingTemplate?.id === id) setEditingTemplate(null);
  };

  const addItemToTemplate = (tpl) => {
    if (!newItemLabel.trim()) return;
    const updated = { ...tpl, items: [...tpl.items, { id: `item_${Date.now()}`, label: newItemLabel.trim(), custom: true }] };
    setEditingTemplate(updated);
    setNewItemLabel('');
  };

  const removeItemFromTemplate = (tpl, itemId) => {
    setEditingTemplate({ ...tpl, items: tpl.items.filter(i => i.id !== itemId) });
  };

  const createBlankTemplate = async () => {
    if (!newTemplateName.trim()) return;
    const res = await fetch(`${API}/api/templates`, {
      method:'POST', credentials:'include',
      headers: authHeaders(true),
      body: JSON.stringify({ name: newTemplateName.trim(), items: [] }),
    });
    const newTpl = await res.json();
    setTemplates(prev => [...prev, newTpl]);
    setEditingTemplate(newTpl);
    setNewTemplateName('');
    setShowNewTemplate(false);
  };

  const setStatusCategory = (name, category) => {
    const cats = { ...(settings.statusCategories || {}) };
    cats[name.toLowerCase()] = category;
    setSettings({ ...settings, statusCategories: cats });
    setSaved(false);
  };

  const getStatusCategory = (name) => {
    const cats = settings.statusCategories || {};
    const cat = cats[name.toLowerCase()];
    if (cat) return cat;
    // Smart defaults based on known status names
    const n = name.toLowerCase();
    if (n === 'done & paid' || n === 'finished' || n === 'finished/paid' || n === 'credit account') return 'closed';
    if (n === 'manager review' || n === 'customer confirm') return 'review';
    return 'active';
  };

  // "Saved" used to be printed the moment the request came back, whatever it
  // said. A rejected write, an expired session, a server that was down: all
  // three showed a green tick, and the settings on screen were the only copy
  // left. A confident answer nobody can tell is wrong -- the same shape as the
  // unfiltered Lightspeed reads in August. Now the response decides.
  const saveSettings = async () => {
    setSaveError(null);
    const touchedQueue = QUEUE_KEYS.some(k =>
      JSON.stringify(settings?.[k]) !== JSON.stringify(savedSnapshotRef.current?.[k]));
    try {
      const res = await fetch(`${API}/api/settings`, { method:'POST', credentials:'include', headers: authHeaders(true), body: JSON.stringify(settings) });
      if (!res.ok) {
        // The route explains itself when it can (an unknown key, a rejected
        // value). Fall back to the status code rather than inventing a reason.
        const detail = await res.json().catch(() => null);
        throw new Error(detail?.error || `The server refused the save (${res.status}).`);
      }
      // Only now is the stored copy known -- the snapshot moves on a success
      // and never on a failure, so a retry still reports the same change.
      savedSnapshotRef.current = settings;
      // Re-rendering on this is what clears the dirty dot in the sidebar; a ref
      // alone would leave the save bar up until something else moved.
      setSavedSettings(settings);
      setQueueAffected(touchedQueue);
      setSaved(true);
      setTimeout(() => setSaved(false), 4000);
    } catch (err) {
      // Includes the network never reaching the server at all, which is the
      // case that looked most like success before.
      setSaved(false);
      setSaveError(err.message || 'Could not reach the server.');
    }
  };

  // Shop, Connections and Users are Administrator-only (17 Sep decision). The
  // server refuses those routes and those keys as well -- this keeps a Manager
  // from being shown cards that would refuse to save.
  const isAdmin = currentUser?.role === 'owner';

  // The open group comes from the sidebar. An unknown id, or an
  // Administrator-only group reached by a Manager, falls back to the first
  // group they may see rather than rendering nothing.
  const wanted = groupById(group);
  const open = (wanted && (!wanted.admin || isAdmin))
    ? wanted
    : SETTINGS_GROUPS.find(g => !g.admin || isAdmin);
  const dirty = !!settings && groupIsDirty(open, settings, savedSettings);

  // App.js owns the sidebar and the unsaved-changes dialog, so it has to know.
  // Everything below this line is hooks-free, which is why this sits above the
  // loading return rather than beside the save bar it describes.
  useEffect(() => {
    if (onDirtyChange) onDirtyChange(dirty ? open.id : null);
  }, [dirty, open.id, onDirtyChange]);

  // "Save and continue" in that dialog lands here: App bumps a counter rather
  // than reaching into this component, and the ref keeps the effect from
  // re-firing every time saveSettings is recreated on a render.
  const saveRef = useRef(saveSettings);
  saveRef.current = saveSettings;
  useEffect(() => { if (saveSignal > 0) saveRef.current(); }, [saveSignal]);

  if (loading) return <div style={{ color:T.textSub, padding:40 }}>Loading settings…</div>;
  if (!settings) return <div style={{ color:T.textSub, padding:40 }}>Could not load settings.</div>;

  const excluded = (settings.excludedStatuses || []).map(e => e.toLowerCase());


  const Group = GROUP_COMPONENTS[open.id];

  // Put back what the server has, for THIS group only. A discard in Payments
  // must not silently undo an unsaved edit sitting in Messages.
  const discardChanges = () => {
    if (!savedSettings) return;
    const restore = {};
    for (const k of open.keys) restore[k] = savedSettings[k];
    setSettings(prev => ({ ...prev, ...restore }));
    setSaveError(null);
  };

  // Handed to the groups below. Same state, same handlers, same functions --
  // they simply live in SettingsPage and are read from there, so splitting the
  // page into files changed no behaviour.
  const ctx = {
    AR_DEFAULT_MSG, T, addItemToTemplate, allStatuses, authStatus, bookingBase,
    bookingItemFilter, bookingItems, copiedLinkId, createBlankTemplate, createTerminal, currentUser,
    deleteTemplate, dirty, disconnectStripe, editingTemplate, excluded, freshKey, getStatusCategory,
    importFromItem, isDark, keyCopied, laborItems, loadBookingItems, loadLaborItems,
    loading, loadingBookingItems, loadingItems, lsDefaults, newBlackoutDate, newItemLabel,
    newTemplateName, newTerminalLabel, parsePasteText, pasteText, paymentTypes, removeItemFromTemplate,
    revokeTerminal, saveSettings, saveTemplate, saved, setAutoReply, setBooking,
    setBookingItemFilter, setBookingItems, setCopiedLinkId, setEditingTemplate, setFreshKey, setKeyCopied,
    setLaborItems, setLightspeed, setNewBlackoutDate, setNewItemLabel, setNewTemplateName, setNewTerminalLabel,
    setPasteText, setPayments, setSaved, setSettings, setShopHoursDay, setShowConnAdvanced,
    setShowNewTemplate, setShowPaste, setShowRevokedTerminals, setStatusCategory, setTerminalError, settings,
    showConnAdvanced, showNewTemplate, showPaste, showRevokedTerminals, stripeBanner, stripeStatus,
    taxClasses, templates, terminalBusy, terminalError, terminals, terminalsLoading,
    theme,
  };

  return (
    <div>
      <div style={{ ...S.topbar, background:T.topbar, display:'flex', alignItems:'baseline', gap:14, flexWrap:'wrap' }}>
        <div style={{ ...S.pageTitle, color:T.text }}>{open.label}</div>
        <div style={{ fontSize:12, color:T.textSub }}>Settings</div>
      </div>
      <div style={{ fontSize:13, color:T.textSub, maxWidth:'70ch', marginTop:-16, marginBottom:20 }}>
        {open.blurb}
      </div>

      <SettingsContext.Provider value={ctx}>
        <Group />
      </SettingsContext.Provider>

      {/* The save bar. It appears only when this group has changes, and sits at
          the bottom of the group rather than in the top bar: the edit and the
          Save are then in the same place, which is what the two-button version
          got wrong. Sticky, so a long group (Online Booking) does not hide it. */}
      {(dirty || saved || saveError) && (
        <div style={{ position:'sticky', bottom:0, marginTop:16, display:'flex', alignItems:'center',
          gap:12, flexWrap:'wrap', background:T.card, border:`1px solid ${T.cardBorder}`, borderBottom:'none',
          borderRadius:'10px 10px 0 0', padding:'12px 18px' }}>
          <div style={{ flex:'1 1 220px', fontSize:13, color:T.text }}>
            {saveError ? (
              <span><strong style={{ color:'#e74c3c' }}>Not saved.</strong> {saveError} Your changes are still on screen — try again.</span>
            ) : saved ? (
              <span style={S.savedMsg}>
                ✓ Saved.{queueAffected ? ' The Service Queue will show this the next time it loads.' : ''}
              </span>
            ) : (
              <span><strong style={{ color:'#e67e22' }}>Unsaved changes</strong> in {open.label}</span>
            )}
          </div>
          {dirty && (
            <button style={{ ...S.btn(false), color:T.textSub, borderColor:T.filterBorder }}
              onClick={discardChanges}>Discard</button>
          )}
          <button style={{ ...S.btn(true), background:T.btnPrimary, color:T.btnPrimaryText }}
            onClick={saveSettings}>{saved ? '✓ Saved' : `Save ${open.label}`}</button>
        </div>
      )}
    </div>
  );
}

// ── Ready for Pickup Page ─────────────────────────────────────────────────────

export default SettingsPage;

